import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePopupStore from "@/hooks/usePopupStore";
import ActionsBar from "@/UI/components/ActionsBar";
import Table, { type TableColumn } from "@/UI/components/Table";
import TableCell from "@/UI/components/Table/TableCell";
import ClientsForm from "@/UI/forms/ClientsForm";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import SidePopup from "@/UI/popups/SidePopup";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

const PAGE_SIZE = 12;
const MODELS = ["branches", "products", "tiers", "challenges", "ad-banners", "clients"] as const;

interface ClientPermission {
    store: string;
    branches: "all" | string[];
    model: string;
    actions: string[];
}

interface Client {
    _id: string;
    name: string;
    phone: string;
    picture: string | null;
    permissions: ClientPermission[];
    isOwner: boolean;
    isScanner: boolean;
}

function getModelActions(permissions: ClientPermission[], model: string): string[] {
    const perm = permissions.find((p) => p.model === model);
    return perm?.actions ?? [];
}

export default function ClientsScreen() {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();
    const [page, setPage] = useState(1);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    // ── Clients fetch ────────────────────────────────────────────────────────────
    const { data, loading, invoke } = useFetch("clients", { query: { page: 1, limit: PAGE_SIZE } });

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        invoke({ query: { page, limit: PAGE_SIZE } });
    }, [page]);

    useEffect(() => {
        const handler = () => {
            setPage(1);
            invoke({ query: { page: 1, limit: PAGE_SIZE } });
        };
        window.addEventListener("clients:refresh", handler);
        return () => window.removeEventListener("clients:refresh", handler);
    }, []);

    const clients: Client[] = data?.data?.clients ?? [];
    const total: number = data?.data?.total ?? 0;

    // ── Delete fetch ──────────────────────────────────────────────────────────
    const { loading: deleting, status: deleteStatus, invoke: deleteInvoke } = useFetch("clients", { method: "DELETE" }, false);

    const wasDeleting = useRef(false);
    useEffect(() => {
        if (deleting) {
            wasDeleting.current = true;
        } else if (wasDeleting.current) {
            wasDeleting.current = false;
            setDeletingId(null);
            if (deleteStatus === 200) invoke({ query: { page, limit: PAGE_SIZE } });
        }
    }, [deleting]);

    const handleDelete = (id: string) => {
        if (deleting) return;
        setDeletingId(id);
        deleteInvoke({ param: id });
    };

    const openDeleteConfirm = (client: Client) => {
        const name = "confirm-delete-client";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message: t.clients.deleteConfirm,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                onConfirm: () => handleDelete(client._id),
            },
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Get-one fetch (edit / duplicate) ──────────────────────────────────────
    const pendingModeRef = useRef<"edit" | "duplicate" | null>(null);
    const { loading: fetchingOne, data: oneData, invoke: fetchOne } = useFetch("clients", { method: "GET" }, false);

    const openClientForm = (mode: "edit" | "duplicate", client: any) => {
        const name = mode === "edit" ? "edit-client" : "duplicate-client";
        const title = mode === "edit" ? t.clientForm.editTitle : t.clientForm.duplicateTitle;
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title,
                Content: ClientsForm,
                contentDetails: {
                    clientId: mode === "edit" ? client._id : undefined,
                    initialData: client,
                    isDuplicate: mode === "duplicate",
                    onSuccess: () => {
                        invoke({ query: { page: 1, limit: PAGE_SIZE } });
                        setPage(1);
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    const wasFetchingOne = useRef(false);
    useEffect(() => {
        if (fetchingOne) {
            wasFetchingOne.current = true;
        } else if (wasFetchingOne.current) {
            wasFetchingOne.current = false;
            const client = oneData?.data?.client;
            if (client && pendingModeRef.current) {
                openClientForm(pendingModeRef.current, client);
                pendingModeRef.current = null;
            }
        }
    }, [fetchingOne]);

    const handleEdit = (row: Client) => {
        pendingModeRef.current = "edit";
        fetchOne({ param: row._id });
    };

    const handleDuplicate = (row: Client) => {
        pendingModeRef.current = "duplicate";
        fetchOne({ param: row._id });
    };

    // ── Add Client ──────────────────────────────────────────────────────────────
    const openAddClientForm = () => {
        const name = "add-client";
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title: t.clientForm.createTitle,
                Content: ClientsForm,
                contentDetails: {
                    onSuccess: () => {
                        window.dispatchEvent(new CustomEvent("clients:refresh"));
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Action badge labels ───────────────────────────────────────────────────
    const actionLabels: Record<string, string> = {
        viewing: t.permissionPopup.viewing,
        creating: t.permissionPopup.creating,
        editing: t.permissionPopup.editing,
        deleting: t.permissionPopup.deleting,
    };

    // ── Columns ───────────────────────────────────────────────────────────────
    const columns: TableColumn<Client>[] = [
        {
            key: "picture",
            header: t.clients.col.picture,
            width: "70px",
            render: (row) => <TableCell.Gallery squared={true} images={row.picture ? [row.picture] : []} alt={row.name} />,
        },
        {
            key: "name",
            header: t.clients.col.name,
            width: "220px",
            render: (row) => <TableCell.Text value={row.name} maxChars={22} />,
        },
        {
            key: "phone",
            header: t.clients.col.phone,
            width: "180px",
            render: (row) => <TableCell.Text direction="ltr" value={row.phone} />,
        },
        {
            key: "role",
            header: t.clients.col.role,
            width: "80px",
            render: (row) => (
                <span className={`clients-role-badge clients-role-badge--${row.isOwner ? "owner" : "staff"}`}>
                    {row.isOwner ? t.clients.owner : t.clients.staff}
                </span>
            ),
        },
        {
            key: "isScanner",
            header: t.clients.col.scanner,
            width: "140px",
            render: (row) =>
                row.isScanner ? (
                    <span className="clients-role-badge clients-role-badge--scanner">{t.clients.scanner}</span>
                ) : (
                    <span className="clients-no-perms">—</span>
                ),
        },
        ...MODELS.map((model) => ({
            key: model,
            header: t.permissionPopup.models[model as keyof typeof t.permissionPopup.models],
            width: "200px",
            render: (row: Client) => {
                const actions = getModelActions(row.permissions, model);
                if (actions.length === 0) {
                    return <span className="clients-no-perms">—</span>;
                }
                return (
                    <div className="clients-action-badges">
                        {actions.map((action) => (
                            <span key={action} className={`clients-action-badge clients-action-badge--${action}`}>
                                {actionLabels[action] || action}
                            </span>
                        ))}
                    </div>
                );
            },
        })),
        {
            key: "actions",
            header: t.clients.col.actions,
            width: "60px",
            render: (row) => (
                <TableCell.Actions
                    disabled={deletingId === row._id || fetchingOne}
                    actions={[
                        { name: "edit", icon: "/icons/edit-icon.svg", handler: () => handleEdit(row) },
                        { name: "duplicate", icon: "/icons/duplicate-icon.svg", handler: () => handleDuplicate(row) },
                        { name: "delete", icon: "/icons/delete-icon.svg", handler: () => openDeleteConfirm(row), danger: true },
                    ]}
                />
            ),
        },
    ];

    const filterContent = <input className="actions-bar-search-input" placeholder={t.actionsBar.search} />;

    return (
        <div className="clients-screen">
            <ActionsBar onAdd={openAddClientForm} filterContent={filterContent} />
            <div className="clients-screen-content">
                <Table
                    columns={columns}
                    data={clients}
                    loading={loading}
                    rowKey="_id"
                    pagination={{ page, pageSize: PAGE_SIZE, total, onPageChange: setPage }}
                />
            </div>
        </div>
    );
}
